import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { releaseFollowUpAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Search, CalendarClock, AlertTriangle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BUCKETS = [
  { value: 'all', label: 'All Open', color: 'bg-slate-100 text-slate-700' },
  { value: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-700' },
  { value: 'today', label: 'Due Today', color: 'bg-amber-100 text-amber-700' },
  { value: 'due_soon', label: 'Due Soon (7d)', color: 'bg-orange-100 text-orange-700' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
];

const WAITING_PERIOD_LABELS = {
  '60_days': '60 Days',
  '90_days': '90 Days',
  '6_months': '6 Months',
  'blockout': 'Blockout',
  'custom': 'Custom',
};

export default function ReleaseFollowUpsPage() {
  const [searchParams] = useSearchParams();
  const initialBucket = (() => {
    const raw = searchParams.get('bucket');
    return ['overdue', 'today', 'due_soon', 'later', 'completed', 'all'].includes(raw) ? raw : 'all';
  })();
  const [bucket, setBucket] = useState(initialBucket);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [data, setData] = useState({ items: [], summary: { overdue: 0, today: 0, due_soon: 0, later: 0, completed: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, debouncedSearch]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { bucket };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await releaseFollowUpAPI.list(params);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load release follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const runScheduler = async () => {
    setRunning(true);
    try {
      const res = await releaseFollowUpAPI.runScheduler();
      toast.success(`Scheduler executed — ${res.data.sent_count} reminder(s) sent`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scheduler failed (SuperAdmin only)');
    } finally {
      setRunning(false);
    }
  };

  const { items, summary } = data;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="release-followups-page">
      <PageHeader
        title="Release Follow-Ups"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Requests', href: '/requests' },
          { label: 'Release Follow-Ups', href: '/release-followups' },
        ]}
        actions={
          <Button variant="outline" onClick={runScheduler} disabled={running} data-testid="run-scheduler-btn">
            <RefreshCw size={14} className={'mr-1.5 ' + (running ? 'animate-spin' : '')} />
            Run reminder check
          </Button>
        }
      />

      <div className="page-content space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <SummaryCard label="Overdue" value={summary.overdue} icon={AlertTriangle} color="bg-red-50 text-red-700" iconColor="text-red-500" onClick={() => setBucket('overdue')} testId="card-overdue" />
          <SummaryCard label="Due Today" value={summary.today} icon={Clock} color="bg-amber-50 text-amber-700" iconColor="text-amber-500" onClick={() => setBucket('today')} testId="card-today" />
          <SummaryCard label="Due Soon (7d)" value={summary.due_soon} icon={CalendarClock} color="bg-orange-50 text-orange-700" iconColor="text-orange-500" onClick={() => setBucket('due_soon')} testId="card-due-soon" />
          <SummaryCard label="Later" value={summary.later} icon={CalendarClock} color="bg-slate-50 text-slate-700" iconColor="text-slate-400" onClick={() => setBucket('all')} testId="card-later" />
          <SummaryCard label="Completed" value={summary.completed} icon={CheckCircle2} color="bg-emerald-50 text-emerald-700" iconColor="text-emerald-500" onClick={() => setBucket('completed')} testId="card-completed" />
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <Tabs value={bucket} onValueChange={setBucket}>
            <TabsList data-testid="bucket-tabs">
              {BUCKETS.map(b => (
                <TabsTrigger key={b.value} value={b.value} data-testid={`tab-${b.value}`}>
                  {b.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative w-full md:w-80">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agent / carrier / agency / notes"
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>NPN</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Waiting Period</TableHead>
                  <TableHead>Release Sent</TableHead>
                  <TableHead>Follow-Up</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">No release follow-ups in this bucket.</TableCell></TableRow>
                ) : items.map((row) => {
                  const isOverdue = !row.completed_at && row.follow_up_date && row.follow_up_date < today;
                  const isDueToday = !row.completed_at && row.follow_up_date === today;
                  const isCompleted = !!row.completed_at;
                  return (
                    <TableRow key={row.request_id} data-testid={`rfu-row-${row.request_id}`}>
                      <TableCell className="font-medium">
                        <Link to={`/requests/${row.request_id}`} className="hover:text-sky-600 hover:underline">
                          {row.agent_name}
                        </Link>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.npn || '-'}</TableCell>
                      <TableCell className="text-sm">{row.agency_name || '-'}</TableCell>
                      <TableCell className="text-sm">{row.carrier_name || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {WAITING_PERIOD_LABELS[row.waiting_period_type] || '-'}
                      </TableCell>
                      <TableCell className="text-sm">{row.release_sent_date ? formatDate(row.release_sent_date) : '-'}</TableCell>
                      <TableCell>
                        <span className={
                          isOverdue ? 'text-red-600 font-semibold' :
                          isDueToday ? 'text-amber-600 font-semibold' :
                          'text-slate-700'
                        }>
                          {row.follow_up_date ? formatDate(row.follow_up_date) : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {isCompleted ? <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge> :
                         isOverdue ? <Badge className="bg-red-100 text-red-700">Overdue</Badge> :
                         isDueToday ? <Badge className="bg-amber-100 text-amber-700">Due Today</Badge> :
                         <Badge variant="outline">{row.bucket?.replace('_', ' ') || 'Pending'}</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link to={`/requests/${row.request_id}`}>
                          <Button variant="outline" size="sm" data-testid={`open-${row.request_id}`}>Open</Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, iconColor, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border bg-white hover:shadow-md transition-shadow p-4 text-left flex items-center justify-between gap-3 ${color}`}
      data-testid={testId}
    >
      <div>
        <p className="text-3xl font-heading font-semibold">{value}</p>
        <p className="text-xs uppercase tracking-wider opacity-80 mt-0.5">{label}</p>
      </div>
      <Icon size={28} className={iconColor} />
    </button>
  );
}
